import { config } from '../config'

export const ACCESS_METHOD_TYPE = {
  DIGITAL: 'DIGITAL',
  PHYSICAL: 'PHYSICAL',
  OTHER_DIGITAL: 'OTHER_DIGITAL',
  OTHER_PHYSICAL: 'OTHER_PHYSICAL',
  OTHER_PICKUP: 'OTHER_PICKUP'
}

const OTHER_ACCESS_METHODS = new Set([ACCESS_METHOD_TYPE.OTHER_DIGITAL, ACCESS_METHOD_TYPE.OTHER_PHYSICAL, ACCESS_METHOD_TYPE.OTHER_PICKUP])
const DIGITAL_LIKE_METHODS = new Set([ACCESS_METHOD_TYPE.DIGITAL, ACCESS_METHOD_TYPE.OTHER_DIGITAL])
const RESPONSE_PACKAGE_METHODS = new Set([ACCESS_METHOD_TYPE.PHYSICAL, ACCESS_METHOD_TYPE.OTHER_PHYSICAL, ACCESS_METHOD_TYPE.OTHER_PICKUP])
const KNOWN_ACCESS_METHODS = new Set(Object.values(ACCESS_METHOD_TYPE))

const DELIVERY_PRESENTATION = {
  editableInstruction:
    'You will need to mail the response or make it available for in person viewing. ' +
    'The response package will be downloadable after marking this FOI as complete.',
  completedInstruction: 'The response can be mailed or made available for in person viewing.',
  confirmationInstruction:
    'You will need to download your final response package and mail it or make it available for in person viewing. ' +
    'Contact details are available on the FOI.'
}

const PICKUP_DELIVERY_PRESENTATION = {
  editableInstruction:
    'You will need to make the response available for pickup. ' + 'The response package will be downloadable after marking this FOI as complete.',
  completedInstruction: 'The response should be picked up in person.',
  confirmationInstruction:
    'You will need to download your final response package and make it available for pickup. ' + 'Contact details are available on the FOI.'
}

function getDefaultAccessMethodConfig() {
  return config.foi.request.accessMethod
}

function getOtherActiveType(accessMethodConfig = getDefaultAccessMethodConfig()) {
  const otherActiveType = accessMethodConfig?.otherActiveType
  if (!otherActiveType) return null
  if (!OTHER_ACCESS_METHODS.has(otherActiveType.type)) return null
  if (typeof otherActiveType.label !== 'string' || otherActiveType.label.trim() === '') return null
  if (otherActiveType.pricingDetails !== undefined && typeof otherActiveType.pricingDetails !== 'string') return null

  return otherActiveType
}

function getLabel(accessMethod, accessMethodConfig = getDefaultAccessMethodConfig()) {
  if (accessMethod === ACCESS_METHOD_TYPE.DIGITAL) {
    return ['Electronic Copy', accessMethodConfig?.digitalLabel ?? ''].join(' ')
  }

  if (accessMethod === ACCESS_METHOD_TYPE.PHYSICAL) {
    return ['Paper Copy', accessMethodConfig?.physicalLabel ?? ''].join(' ')
  }

  if (OTHER_ACCESS_METHODS.has(accessMethod)) {
    const otherActiveType = getOtherActiveType(accessMethodConfig)
    return otherActiveType?.type === accessMethod ? otherActiveType.label : 'Unknown'
  }

  return 'Unknown'
}

function getSelectionLabel(accessMethod, accessMethodConfig = getDefaultAccessMethodConfig()) {
  const label = getLabel(accessMethod, accessMethodConfig)
  if (!OTHER_ACCESS_METHODS.has(accessMethod) || label === 'Unknown') return label

  const pricingDetails = getOtherActiveType(accessMethodConfig)?.pricingDetails?.trim()
  return [label.trim(), pricingDetails].filter(Boolean).join(' ')
}

function assertKnownAccessMethod(accessMethod) {
  if (KNOWN_ACCESS_METHODS.has(accessMethod)) return

  throw new Error(`Unknown FOI access method: ${accessMethod}`)
}

function isDigitalLike(accessMethod) {
  assertKnownAccessMethod(accessMethod)
  return DIGITAL_LIKE_METHODS.has(accessMethod)
}

function requiresResponsePackage(accessMethod) {
  assertKnownAccessMethod(accessMethod)
  return RESPONSE_PACKAGE_METHODS.has(accessMethod)
}

function getDeliveryWarningPresentation(accessMethod, accessMethodConfig = getDefaultAccessMethodConfig()) {
  assertKnownAccessMethod(accessMethod)
  if (isDigitalLike(accessMethod)) return null

  return {
    label: accessMethod === ACCESS_METHOD_TYPE.PHYSICAL ? 'paper copy' : getLabel(accessMethod, accessMethodConfig),
    ...(accessMethod === ACCESS_METHOD_TYPE.OTHER_PICKUP ? PICKUP_DELIVERY_PRESENTATION : DELIVERY_PRESENTATION)
  }
}

export default {
  getOtherActiveType,
  getDeliveryWarningPresentation,
  getLabel,
  getSelectionLabel,
  isDigitalLike,
  requiresResponsePackage
}
